import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, FileText, Clock, CheckCircle, AlertCircle, Play, Eye, Edit, Trash2, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [calendar, setCalendar] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState('calendar');

  useEffect(() => {
    fetchData();
  }, [currentPage]);

  const fetchData = async () => {
    try {
      const [statsRes, calendarRes, draftsRes, scheduledRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/content-calendar?page=${currentPage}&limit=30`),
        axios.get(`${API}/admin/drafts`),
        axios.get(`${API}/admin/scheduled`)
      ]);
      
      setStats(statsRes.data);
      setCalendar(calendarRes.data.items);
      setTotalPages(calendarRes.data.total_pages);
      setDrafts(draftsRes.data.articles);
      setScheduled(scheduledRes.data.articles);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateArticle = async (day) => {
    setGenerating(true);
    try {
      const topic = calendar.find(c => c.day === day);
      if (!topic) return;
      
      await axios.post(`${API}/admin/generate-article`, {
        topic: topic.topic,
        category: topic.category,
        keywords: topic.keywords,
        author: topic.author,
        day_number: day
      });
      
      alert('Article generated successfully!');
      fetchData();
    } catch (error) {
      console.error('Error generating article:', error);
      alert('Error generating article: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const generateBatch = async (startDay, count = 7) => {
    setGenerating(true);
    try {
      const response = await axios.post(`${API}/admin/generate-batch?start_day=${startDay}&count=${count}`);
      alert(`Generated ${response.data.generated} articles! ${response.data.failed} failed.`);
      fetchData();
    } catch (error) {
      console.error('Error generating batch:', error);
      alert('Error generating batch: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const publishArticle = async (articleId) => {
    try {
      await axios.post(`${API}/admin/article/${articleId}/publish`);
      alert('Article published!');
      fetchData();
    } catch (error) {
      console.error('Error publishing:', error);
      alert('Error publishing article');
    }
  };

  const deleteArticle = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) return;
    
    try {
      await axios.delete(`${API}/admin/article/${articleId}`);
      fetchData();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Error deleting article');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-8 h-8 border-2 border-stone-300 border-t-stone-900 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="admin-dashboard">
      {/* Header */}
      <div className="bg-stone-900 text-white py-6">
        <div className="container-luxury">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-medium" style={{fontFamily: "'Playfair Display', serif"}}>
                Content Admin Dashboard
              </h1>
              <p className="text-stone-400 mt-1">365-Day E-E-A-T Article Generator</p>
            </div>
            <Link to="/" className="text-stone-400 hover:text-white transition-colors">
              ← Back to Site
            </Link>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="container-luxury py-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white p-6 border border-stone-200 text-center">
            <p className="text-3xl font-medium text-stone-900">{stats?.total_calendar_days || 365}</p>
            <p className="text-sm text-stone-500 mt-1">Total Days</p>
          </div>
          <div className="bg-white p-6 border border-stone-200 text-center">
            <p className="text-3xl font-medium text-amber-600">{stats?.pending || 0}</p>
            <p className="text-sm text-stone-500 mt-1">Pending</p>
          </div>
          <div className="bg-white p-6 border border-stone-200 text-center">
            <p className="text-3xl font-medium text-blue-600">{stats?.drafts || 0}</p>
            <p className="text-sm text-stone-500 mt-1">Drafts</p>
          </div>
          <div className="bg-white p-6 border border-stone-200 text-center">
            <p className="text-3xl font-medium text-purple-600">{stats?.scheduled || 0}</p>
            <p className="text-sm text-stone-500 mt-1">Scheduled</p>
          </div>
          <div className="bg-white p-6 border border-stone-200 text-center">
            <p className="text-3xl font-medium text-green-600">{stats?.published || 0}</p>
            <p className="text-sm text-stone-500 mt-1">Published</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-stone-200">
          <button
            onClick={() => setActiveTab('calendar')}
            className={`pb-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'calendar' 
                ? 'text-amber-800 border-b-2 border-amber-800' 
                : 'text-stone-500 hover:text-stone-900'
            }`}
          >
            <Calendar className="w-4 h-4 inline mr-2" />
            Content Calendar
          </button>
          <button
            onClick={() => setActiveTab('drafts')}
            className={`pb-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'drafts' 
                ? 'text-amber-800 border-b-2 border-amber-800' 
                : 'text-stone-500 hover:text-stone-900'
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Drafts ({drafts.length})
          </button>
          <button
            onClick={() => setActiveTab('scheduled')}
            className={`pb-3 px-4 text-sm font-medium transition-colors ${
              activeTab === 'scheduled' 
                ? 'text-amber-800 border-b-2 border-amber-800' 
                : 'text-stone-500 hover:text-stone-900'
            }`}
          >
            <Clock className="w-4 h-4 inline mr-2" />
            Scheduled ({scheduled.length})
          </button>
        </div>

        {/* Content Calendar Tab */}
        {activeTab === 'calendar' && (
          <div className="bg-white border border-stone-200">
            <div className="p-4 border-b border-stone-200 flex items-center justify-between">
              <h2 className="font-medium text-stone-900">Content Calendar</h2>
              <button
                onClick={() => generateBatch((currentPage - 1) * 30 + 1, 7)}
                disabled={generating}
                className="btn-primary text-sm flex items-center gap-2"
              >
                {generating ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                Generate Next 7 Days
              </button>
            </div>
            
            <div className="divide-y divide-stone-100">
              {calendar.map((item) => (
                <div key={item.day} className="p-4 flex items-center gap-4 hover:bg-stone-50">
                  <div className="w-12 h-12 bg-stone-100 flex items-center justify-center text-lg font-medium text-stone-600">
                    {item.day}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-900 truncate">{item.topic}</p>
                    <p className="text-sm text-stone-500">
                      {item.category} · {item.author}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {item.status === 'pending' && (
                      <button
                        onClick={() => generateArticle(item.day)}
                        disabled={generating}
                        className="px-3 py-1 bg-amber-100 text-amber-800 text-sm hover:bg-amber-200 transition-colors flex items-center gap-1"
                      >
                        <Play className="w-3 h-3" />
                        Generate
                      </button>
                    )}
                    {item.status === 'draft' && (
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        Draft
                      </span>
                    )}
                    {item.status === 'scheduled' && (
                      <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Scheduled
                      </span>
                    )}
                    {item.status === 'published' && (
                      <span className="px-3 py-1 bg-green-100 text-green-800 text-sm flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Published
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="p-4 border-t border-stone-200 flex items-center justify-between">
              <p className="text-sm text-stone-500">
                Page {currentPage} of {totalPages}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-2 border border-stone-200 hover:bg-stone-50 disabled:opacity-50"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-2 border border-stone-200 hover:bg-stone-50 disabled:opacity-50"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Drafts Tab */}
        {activeTab === 'drafts' && (
          <div className="bg-white border border-stone-200">
            <div className="p-4 border-b border-stone-200">
              <h2 className="font-medium text-stone-900">Draft Articles for Review</h2>
            </div>
            
            {drafts.length === 0 ? (
              <div className="p-12 text-center text-stone-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No draft articles yet. Generate some from the calendar!</p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {drafts.map((article) => (
                  <div key={article.id} className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-stone-900 mb-1">{article.title}</h3>
                        <p className="text-sm text-stone-500 mb-2">{article.excerpt}</p>
                        <p className="text-xs text-stone-400">
                          By {article.author_name} · {article.category} · Day {article.day_number}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => window.open(`/admin/preview/${article.id}`, '_blank')}
                          className="p-2 text-stone-500 hover:text-stone-900 hover:bg-stone-100"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => publishArticle(article.id)}
                          className="px-3 py-1 bg-green-100 text-green-800 text-sm hover:bg-green-200 transition-colors"
                        >
                          Publish
                        </button>
                        <button
                          onClick={() => deleteArticle(article.id)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Scheduled Tab */}
        {activeTab === 'scheduled' && (
          <div className="bg-white border border-stone-200">
            <div className="p-4 border-b border-stone-200">
              <h2 className="font-medium text-stone-900">Scheduled Articles</h2>
            </div>
            
            {scheduled.length === 0 ? (
              <div className="p-12 text-center text-stone-500">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No scheduled articles. Schedule drafts from the Drafts tab!</p>
              </div>
            ) : (
              <div className="divide-y divide-stone-100">
                {scheduled.map((article) => (
                  <div key={article.id} className="p-4 flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-stone-900">{article.title}</h3>
                      <p className="text-sm text-stone-500">
                        Scheduled for: {new Date(article.scheduled_for).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => publishArticle(article.id)}
                      className="px-3 py-1 bg-green-100 text-green-800 text-sm hover:bg-green-200"
                    >
                      Publish Now
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
